import dayjs from "dayjs"
import { IconButton, Typography } from "@mui/material"
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import { amber, green, grey } from "@mui/material/colors"
import { numberFormat } from "utils"
import { jobOrderColNm } from "constants/jobOrderConstant"
import { pettyCashColNm } from "../PettyCash/constant"
import { shipmentColNm } from "constants/shipmentConstant"
import { renderEditCellAutocomplete } from "components/DataGridCellExpand/renderEditCellAutocomplete"
import { ArticleRounded, ImageRounded, DragIndicatorRounded } from "resource/muiImport"
import PopoverSearchJobOrder from "components/PopoverSerachJobOrder/PopoverSearchJobOrder"
import { BoxFR } from "components/BoxCustom"

// PayAmnt: strip VAT to get the pre-VAT base, then deduct WHT.
// base = Amnt*100/(100+vat%); pay = base*(1 - wht%).
export const calcPayAmnt = (amnt, vatPrct, whtPrct) => {
  const a = Number(amnt || 0)
  const v = Number(vatPrct || 0)
  const w = Number(whtPrct || 0)
  const base = a * 100 / (100 + v)
  return base * (1 - w / 100)
}

// Receipt no. duplication highlight.
// DupDocNoCnt comes from the server (same DocNo already exists on another pc_item);
// dupDocNos covers rows that duplicate each other inside the current grid only.
export const getDupDocNoSet = (rows) => {
  const count = new Map()
  for (const row of rows) {
    const docNo = (row.RcptNo || "").toString().trim()
    if (!docNo) continue
    count.set(docNo, (count.get(docNo) || 0) + 1)
  }
  return new Set([...count.entries()].filter(([, cnt]) => cnt > 1).map(([docNo]) => docNo))
}

const dupDocNoCellClassName = (dupDocNos) => ({ row, value }) => {
  const docNo = (value || "").toString().trim()
  return (row.DupDocNoCnt > 0 || (docNo && dupDocNos.has(docNo))) ? "cell-dup-docno" : ""
}

export const dupDocNoSx = {
  '& .cell-dup-docno': {
    bgcolor: amber[200],
    fontWeight: "bold",
  },
}

const whatPayrTypOptions = [
  { value: null, label: "-" },
  { value: "A", label: "กระทำการแทน" },
  { value: "O", label: "บริษัททำการหัก" },
  { value: "C", label: "ลูกค้าทำการหัก" }
]

export const driverReceiptColumns = ({ expCombo = [], supplierCombo = [], onImageClick, dupDocNos = new Set() }) => [
  {
    field: "ShowDetail",
    headerName: "",
    width: 40,
    align: 'center',
    renderCell: ({ row }) => (
      <IconButton size="small" disabled={!row.PCTrnsId}>
        <ArticleRounded color={row.PCTrnsId ? "primary" : "disabled"} />
      </IconButton>
    )
  },
  {
    field: "No",
    headerName: "No.",
    width: 54,
    align: 'center',
  },
  {
    field: "PCTrnsId",
    headerName: "บันทึก",
    width: 40,
    align: 'center',
    renderCell: ({ value }) => (
      <CheckCircleRoundedIcon sx={{ color: value ? green[600] : grey[300] }} />
    )
  },
  {
    field: "OrigFileNm",
    headerName: "รูป",
    width: 40,
    renderCell: ({ value, row }) => {
      if (!value) return "-"
      if (!row.FileNm) return value
      const url = row.PCItmId
        ? `/file/pc-item/${row.PCItmId}/${row.FileNm}`
        : `/file/pc-item/Driver/${row.FileNm}`
      return (
        <IconButton
          onClick={(e) => {
            e.stopPropagation()
            onImageClick?.(url)
          }}
        >
          <ImageRounded color="primary" />
        </IconButton>
      )
    },
  },
  {
    field: "PCItmTm",
    headerName: pettyCashColNm.PCItmTm,
    width: 100,
    valueFormatter: ({ value }) => value ? dayjs(value).format("DD/MM HH:mm") : "-",
  },
  {
    field: "UsrNNm",
    headerName: "ผู้บันทึก",
    width: 100,
    renderCell: ({ value, row }) => value ? row.UsrAccTypId === 1 ? null: (
      <Typography
        variant="body2"
        sx={{ cursor: "pointer", color: "primary.main", textDecoration: "underline" }}
      >
        {value}
      </Typography>
    ) : "-",
  },
  {
    field: "ExpId",
    headerName: pettyCashColNm.ExpNm,
    width: 100,
    valueFormatter: ({ value }) => expCombo.find(v => v.id === value)?.label || "-",
    renderEditCell: renderEditCellAutocomplete(expCombo),
    editable: true,
  },
  {
    field: "Amnt",
    headerName: pettyCashColNm.Amnt,
    width: 80,
    type: "number",
    align: "right",
    editable: true,
    valueFormatter: ({ value }) => value ? numberFormat(value) : "-",
  },
  {
    field: "VatPrct",
    headerName: pettyCashColNm.VatPrct,
    width: 55,
    type: "number",
    align: "right",
    editable: true,
    valueFormatter: ({ value }) => `${value || 0}%`,
  },
  {
    field: "WhtPrct",
    headerName: pettyCashColNm.WhtPrct,
    width: 55,
    type: "number",
    align: "right",
    editable: true,
    valueFormatter: ({ value }) => `${value || 0}%`,
  },
  {
    field: "PayAmnt",
    headerName: pettyCashColNm.PayAmnt,
    width: 90,
    type: "number",
    align: "right",
    valueGetter: ({ row }) => calcPayAmnt(row.Amnt, row.VatPrct, row.WhtPrct),
    valueFormatter: ({ value }) => value ? numberFormat(value) : "-",
  },
  {
    field: "RcptNo",
    headerName: pettyCashColNm.DocNo,
    width: 110,
    editable: true,
    cellClassName: dupDocNoCellClassName(dupDocNos),
  },
  {
    field: "RcptDte",
    headerName: pettyCashColNm.DocDte,
    width: 80,
    type: "date",
    editable: true,
    valueFormatter: ({ value }) => value ? dayjs(value).format("DD/MM/YY") : "-",
  },
  {
    field: "RcptNm",
    headerName: "ชื่อใบเสร็จ",
    width: 120,
  },
  {
    field: "RcptAddr",
    headerName: "ที่อยู่ใบเสร็จ",
    width: 130,
  },
  {
    field: "RcptBrchNo",
    headerName: "สาขาใบเสร็จ",
    width: 60,
  },
  {
    field: "RcptTaxId",
    headerName: "TaxId",
    width: 130,
  },
  {
    field: "JobSNm",
    headerName: shipmentColNm.JobSNm,
    width: 100,
  },
  {
    field: "JobRcptBrchNo",
    headerName: "สาขาใบเสร็จ(งาน)",
    valueGetter: ({ row }) => row.JobRcptBrchNo != null ? row.JobRcptBrchNo.toString().padStart(5, '0') : "-",
    width: 60,
  },
  {
    field: "JobRcptTaxId",
    headerName: "TaxId(งาน)",
    width: 130,
  },
  {
    field: "RcptContNo",
    headerName: "ตู้ (ใบเสร็จ)",
    width: 110,
  },
  {
    field: "ContNo",
    headerName: jobOrderColNm.ContNo,
    width: 110,
    // editing it searches job_order (>= 4 chars, appointment within 6 months) and re-links JobOrdId
    editable: true,
  },
  {
    field: "JobOrdId",
    headerName: jobOrderColNm.JobOrdId,
    width: 90,
    type: "number",
    editable: true,
  },
  {
    field: "JobNo",
    headerName: jobOrderColNm.JobNo,
    width: 120,
  },
  // {
  //   field: "Bkg",
  //   headerName: jobOrderColNm.Bkg,
  //   width: 120,
  // },
  // {
  //   field: "CusSNm",
  //   headerName: shipmentColNm.CusSNm,
  //   width: 130,
  // },
  {
    field: "SupId",
    headerName: pettyCashColNm.SupId,
    width: 130,
    editable: true,
    renderEditCell: renderEditCellAutocomplete(supplierCombo),
    // fall back to the name read from the receipt when no supplier record is linked
    valueFormatter: ({ value, api, id }) => supplierCombo.find(v => v.id === value)?.label
      || api?.getRow(id)?.SupNm || "-",
  },
]

// Columns for the "upload receipt from user" dialog grid
// (editable: ExpId, Amnt, DocNo, DocDte, JobOrdId, SupId, ContNo)
export const uploadReceiptColumns = ({ expCombo = [], supplierCombo = [], onImageClick, dupDocNos = new Set() }) => [
  {
    field: "No",
    headerName: "No.",
    width: 50,
    align: 'center',
  },
  {
    field: "OrigFileNm",
    headerName: "รูป",
    width: 40,
    renderCell: ({ row }) => {
      if (!row.FileNm || !row.PCItmId) return "-"
      const url = `/file/pc-item/${row.PCItmId}/${row.FileNm}`
      return (
        <IconButton onClick={(e) => { e.stopPropagation(); onImageClick?.(url) }}>
          <ImageRounded color="primary" />
        </IconButton>
      )
    },
  },
  {
    field: "ExpId",
    headerName: pettyCashColNm.ExpNm,
    width: 120,
    valueFormatter: ({ value }) => expCombo.find(v => v.id === value)?.label || "-",
    renderEditCell: renderEditCellAutocomplete(expCombo),
    editable: true,
  },
  {
    field: "Amnt",
    headerName: pettyCashColNm.Amnt,
    width: 90,
    type: "number",
    align: "right",
    editable: true,
    valueFormatter: ({ value }) => value ? numberFormat(value) : "-",
  },
  {
    field: "RcptNo",
    headerName: pettyCashColNm.DocNo,
    width: 120,
    editable: true,
    cellClassName: dupDocNoCellClassName(dupDocNos),
  },
  {
    field: "RcptDte",
    headerName: pettyCashColNm.DocDte,
    width: 90,
    type: "date",
    editable: true,
    valueFormatter: ({ value }) => value ? dayjs(value).format("DD/MM/YY") : "-",
  },
  {
    field: "RcptContNo",
    headerName: "ตู้ (ใบเสร็จ)",
    width: 110,
  },
  {
    field: "JobOrdId",
    headerName: jobOrderColNm.JobOrdId,
    width: 90,
    type: "number",
    editable: true,
  },
  {
    field: "ContNo",
    headerName: jobOrderColNm.ContNo,
    width: 110,
    // editing it searches job_order (>= 4 chars, appointment within 6 months) and re-links JobOrdId
    editable: true,
  },
  {
    field: "JobNo",
    headerName: jobOrderColNm.JobNo,
    width: 120,
  },
  {
    field: "JobSNm",
    headerName: jobOrderColNm.JobSNm,
    width: 100,
  },
  {
    field: "SupId",
    headerName: pettyCashColNm.SupId,
    width: 130,
    editable: true,
    renderEditCell: renderEditCellAutocomplete(supplierCombo),
    // fall back to the name read from the receipt when no supplier record is linked
    valueFormatter: ({ value, api, id }) => supplierCombo.find(v => v.id === value)?.label
      || api?.getRow(id)?.SupNm || "-",
  },
  {
    field: "error",
    headerName: "สถานะ",
    width: 150,
    renderCell: ({ value }) => value
      ? <Typography variant="body2" color="error">{value}</Typography>
      : <Typography variant="body2" color="success.main">อ่านสำเร็จ</Typography>,
  },
]

export const pcItmMatchColumns = ({ expCombo = [], whtTypeCombo = [], supplierCombo = [], onImageClick
  , handlePopoverSearchOpen, handleSelectJobOrder, suggestDrvId }) => [
  {
    field: "DragHandle",
    headerName: "",
    width: 36,
    align: "center",
    sortable: false,
    renderCell: () => (
      <DragIndicatorRounded sx={{ color: grey[500], cursor: "grab" }} />
    ),
  },
  {
    field: "OrigFileNm",
    headerName: "รูป",
    width: 40,
    renderCell: ({ value, row }) => {
      if (!value) return "-"
      if (!row.FileNm) return value
      const url = row.PCItmId
        ? `/file/pc-item/${row.PCItmId}/${row.FileNm}`
        : `/file/pc-item/Driver/${row.FileNm}`
      return (
        <IconButton
          onClick={(e) => {
            e.stopPropagation()
            onImageClick?.(url)
          }}
        >
          <ImageRounded color="primary" />
        </IconButton>
      )
    },
  },
  {
    field: "No",
    headerName: "No.",
    width: 50,
    align: 'center',
  }, {
    field: "PCItmTm",
    headerName: pettyCashColNm.PCItmTm,
    width: 100,
    valueFormatter: ({ value }) => value ? dayjs(value).format("DD/MM HH:mm") : "-",
  },
  {
    field: "ExpId",
    headerName: pettyCashColNm.ExpNm,
    width: 110,
    valueFormatter: ({ value }) => expCombo.find(v => v.id === value)?.label || "-",
    renderEditCell: renderEditCellAutocomplete(expCombo),
    editable: true,
  },
  {
    field: "AmntB4Vat",
    headerName: pettyCashColNm.AmntB4Vat,
    width: 90,
    type: "number",
    align: "right",
    editable: true,
    valueFormatter: ({ value }) => value ? numberFormat(value) : "0.00",
  },
  {
    field: "VatPrct",
    headerName: pettyCashColNm.VatPrct,
    width: 50,
    type: "number",
    align: "right",
    editable: true,
    valueGetter: ({ value }) => `${value || 0}%`,
  },
  {
    field: "Amnt",
    headerName: pettyCashColNm.Amnt,
    width: 90,
    type: "number",
    align: "right",
    editable: true,
    valueFormatter: ({ value }) => value ? numberFormat(value) : "0.00",
  },
  {
    field: "WhtPrct",
    headerName: pettyCashColNm.WhtPrct,
    width: 50,
    type: "number",
    align: "right",
    editable: true,
    valueGetter: ({ value }) => `${value || 0}%`,
  },
  {
    field: "PayAmnt",
    headerName: pettyCashColNm.PayAmnt,
    width: 90,
    type: "number",
    align: "right",
    valueFormatter: ({ value }) => value ? numberFormat(value) : "0.00",
  },
  {
    field: "WhtPayrTyp",
    headerName: pettyCashColNm.WhtPayrTyp,
    width: 90,
    type: "singleSelect",
    valueOptions: whatPayrTypOptions,
    valueGetter: ({ value }) => whatPayrTypOptions.find(v => v.value === value)?.label || "",
    editable: true,
  },
  {
    field: "SupId",
    headerName: pettyCashColNm.SupId,
    width: 150,
    valueFormatter: ({ value }) => supplierCombo.find(v => v.id === value)?.label || "",
    renderEditCell: renderEditCellAutocomplete(supplierCombo),
    editable: true,
  },
  {
    field: "JobOrdId",
    headerName: jobOrderColNm.JobOrdId,
    width: 110,
    type: "number",
    editable: true,
    align: "left",
    renderCell: ({ id, value }) => (
      <BoxFR sx={{ gap: 0 }}>
        <PopoverSearchJobOrder onOpen={handlePopoverSearchOpen?.(id)}
          handleSelectJobOrder={handleSelectJobOrder}
          suggestDrvId={suggestDrvId} />
        {value || ""}
      </BoxFR>
    ),
  },
  { field: "JobSNm", headerName: jobOrderColNm.JobSNm, width: 100 },
  {
    field: "ContNo",
    headerName: jobOrderColNm.ContNo,
    width: 115,
  },
  {
    field: "JobNo",
    headerName: jobOrderColNm.JobNo,
    width: 120,
  },
  {
    field: "RcptNo",
    headerName: pettyCashColNm.DocNo,
    width: 100,
  },
  {
    field: "RcptDte",
    headerName: pettyCashColNm.DocDte,
    width: 100,
    type: "date",
    valueFormatter: ({ value }) => value ? dayjs(value).format("DD/MM/YY") : "-",
  },
  {
    field: "RcptBrchNo",
    headerName: "สาขาใบเสร็จ",
    width: 60,
  },
  {
    field: "RcptTaxId",
    headerName: "TaxId",
    width: 130,
  },
  {
    field: "JobRcptBrchNo",
    headerName: "สาขาใบเสร็จ(งาน)",
    valueGetter: ({ row }) => row.JobRcptBrchNo != null ? row.JobRcptBrchNo.toString().padStart(5, '0') : "-",
    width: 60,
  },
  {
    field: "JobRcptTaxId",
    headerName: "TaxId(งาน)",
    width: 130,
  },
  {
    field: "WhtTypId",
    headerName: pettyCashColNm.WhtTypId,
    width: 80,
    type: "singleSelect",
    valueOptions: whtTypeCombo.map(v => ({ value: v.id, label: v.label })),
    valueGetter: ({ value }) => whtTypeCombo.find(v => v.id === value)?.label || "",
    editable: true,
  },
]

export const notIsCmptColumns = [
  {
    field: 'ShowDetail',
    headerName: '',
    width: 40,
    align: 'center',
    renderCell: (params) => {
      return (
        <IconButton size="small" >
          <ArticleRounded color="primary" />
        </IconButton>
      )
    }
  },
  { field: "No", headerName: "No.", width: 50, align: "center" },
  // {
  //   field: "IsCmpt", headerName: "ครบ", width: 50, align: "center",
  //   renderCell: ({ value }) => value ? "✓" : "",
  // },
  {
    field: "PayTm", headerName: pettyCashColNm.PayTm, width: 100,
    valueFormatter: ({ value }) => value ? dayjs(value).format("DD/MM HH:mm") : "-",
  },
  { field: "ExpNm", headerName: pettyCashColNm.ExpNm, width: 130 },
  { field: "Dscp", headerName: pettyCashColNm.Dscp, flex: 1 },
  {
    field: "Amnt", headerName: pettyCashColNm.Amnt, width: 90, type: "number", align: "right",
    valueFormatter: ({ value }) => value ? numberFormat(value) : "-",
  },
  {
    field: "JobOrdId",
    headerName: jobOrderColNm.JobOrdId,
    width: 90,
  },
  { field: "TakePlc", headerName: jobOrderColNm.TakePlc, width: 100 },
  { field: "Loc", headerName: jobOrderColNm.Loc, width: 100 },
  { field: "RtnPlc", headerName: jobOrderColNm.RtnPlc, width: 100 },
  { field: "JobSNm", headerName: jobOrderColNm.JobSNm, width: 100 },
  { field: "ContNo", headerName: jobOrderColNm.ContNo, width: 110 },
  { field: "JobNo", headerName: jobOrderColNm.JobNo, width: 110 },
  {
    field: "LeftAmnt", headerName: "คงเหลือ", width: 90, type: "number", align: "right",
    valueFormatter: ({ value }) => value != null ? numberFormat(value) : "-",
  },
]